import { useState, useEffect } from "react";
import {
  Button,
  Box,
  Modal,
  Typography,
  TextField,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import { useNavigate } from "react-router-dom";

// Supabase
import supabase from "../../supabaseClient";

// i18n
import { useTranslation } from "react-i18next";

// Components
import ConfirmationModal from "../utils/ConfirmationModal";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "80%", md: "60%", lg: "50%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

const emptyFormData = {
  first_name: "",
  last_name: "",
  nickname: "",
  description: "",
  birthday: "",
  countrycode_id: "MEX",
  phone: "",
  groups_ids: [],
  birthday_template_id: "",
};

export default function Contact({
  open,
  onClose,
  isEditing,
  contact,
  planData,
  setPlansModalOpen,
  onSubmit,
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyFormData);
  const [countryCodes, setCountryCodes] = useState([]);
  const [variables, setVariables] = useState([]);
  const [newFieldName, setNewFieldName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [variableToDelete, setVariableToDelete] = useState(null);
  const [deleteVariableModalOpen, setDeleteVariableModalOpen] = useState(false);
  const [tooltipOpen1, setTooltipOpen1] = useState(false);
  const [tooltipOpen2, setTooltipOpen2] = useState(false);

  // Context
  const { teamId } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCountryCodes = async () => {
      const { data, error } = await supabase
        .from("countrycodes")
        .select("*")
        .order("country_iso", { ascending: true });

      if (error) {
        showNotification(
          `${t("contact.errorFetchingCountryCodes")}: ${error.message}`,
          "error"
        );
      } else {
        setCountryCodes(data);
      }
    };

    fetchCountryCodes();
  }, []);

  useEffect(() => {
    if (isEditing && contact) {
      const contactVariables = contact.variables || {};
      setFormData({
        first_name: contact.first_name,
        last_name: contact.last_name,
        nickname: contact.nickname,
        description: contact.description,
        birthday: contact.birthday,
        countrycode_id: contact.countrycode_id,
        phone: contact.phone,
        groups_ids: contact.groups_ids,
        birthday_template_id: contact.birthday_template_id?.id || "",
        ...contactVariables,
      });
    } else {
      setFormData(emptyFormData);
    }
  }, [isEditing, contact]);

  useEffect(() => {
    const fetchVariables = async () => {
      const { data, error } = await supabase.from("variables").select("*");

      if (!error) {
        setVariables(data);
      }

      const { data: templatesData, error: templatesError } = await supabase
        .from("templates")
        .select("id, name");

      if (templatesError) {
        showNotification(
          `${t("contact.errorFetchingTemplates")}: ${templatesError.message}`,
          "error"
        );
      } else {
        setTemplates(templatesData);
      }
    };

    fetchVariables();
  }, []);

  const handleChange = (field, value) => {
    if (
      field === "birthday_template_id" &&
      value &&
      !planData.birthday_messages
    ) {
      setPlansModalOpen(true);
      showNotification(t("contact.birthdayMessagesNotSupported"), "warning");
      setFormData({ ...formData, [field]: "" });
      return;
    }
    setFormData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleSubmit = async (e) => {
    trackEvent("contact-form_submit");
    e.preventDefault();
    try {
      const variablesData = {};
      variables.forEach((variable) => {
        variablesData[variable.key] = formData[variable.key];
      });

      const formDataWithoutVariables = { ...formData };
      variables.forEach((variable) => {
        delete formDataWithoutVariables[variable.key];
      });

      if (isEditing) {
        const { error } = await supabase
          .from("contacts")
          .update({
            ...formDataWithoutVariables,
            variables: variablesData,
            birthday: formDataWithoutVariables.birthday || null,
            birthday_template_id:
              formDataWithoutVariables.birthday_template_id || null,
          })
          .eq("id", contact.id);

        if (error) {
          throw error;
        }

        showNotification(t("contact.contactUpdatedSuccess"), "success");
      } else {
        const { error } = await supabase.from("contacts").insert([
          {
            ...formDataWithoutVariables,
            team_id: teamId,
            birthday: formDataWithoutVariables.birthday || null,
            birthday_template_id:
              formDataWithoutVariables.birthday_template_id || null,
            variables: variablesData,
          },
        ]);

        if (error) {
          throw error;
        }

        showNotification(t("contact.contactAddedSuccess"), "success");
      }
      setFormData(emptyFormData);
      onClose();
      onSubmit();
    } catch (error) {
      showNotification(
        `${t("contact.errorOnSubmit")}: ${error.message}`,
        "error"
      );
    }
  };

  const handleAddField = async () => {
    trackEvent("contact-add_field");
    if (newFieldName) {
      try {
        const formattedFieldName = newFieldName
          .toLowerCase()
          .replace(/ /g, "_");

        const { data, error } = await supabase
          .from("variables")
          .insert([
            {
              key: formattedFieldName,
              team_id: teamId,
            },
          ])
          .select();

        if (error) {
          throw error;
        }

        setVariables([
          ...variables,
          { id: data[0].id, key: formattedFieldName },
        ]);

        setNewFieldName("");
      } catch (error) {
        showNotification(
          `${t("contact.errorAddingVariable")}: ${error.message}`,
          "error"
        );
      }
    } else {
      showNotification(t("contact.enterValidVariable"), "warning");
    }
  };

  const handleDeleteVariable = (id) => {
    setDeleteVariableModalOpen(true);
    setVariableToDelete(id);
  };

  const handleDeleteVariableConfirmation = async () => {
    setDeleteVariableModalOpen(false);
    await supabase.from("variables").delete().eq("id", variableToDelete);
    setVariables(() =>
      variables.filter((variable) => variable.id !== variableToDelete)
    );
    setVariableToDelete(null);
  };

  const handleNewTemplateClick = () => {
    trackEvent("contact-new_template_clicked");
    navigate("/dashboard/templates");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Form fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("contact.firstName")}
                value={formData.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
              />
            </Grid>

            {/* More Grid items... */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("contact.lastName")}
                value={formData.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("contact.nickname")}
                value={formData.nickname}
                onChange={(e) => handleChange("nickname", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("contact.description")}
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("contact.birthday")}
                type="date"
                value={formData.birthday}
                onChange={(e) => handleChange("birthday", e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="birthday-template-label">
                  {t("contact.birthdayTemplate")}
                </InputLabel>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Select
                    labelId="birthday-template-label"
                    id="birthday-template"
                    value={formData.birthday_template_id || ""}
                    label={t("contact.birthdayTemplate")}
                    onChange={(e) =>
                      handleChange("birthday_template_id", e.target.value)
                    }
                    sx={{ flexGrow: 1 }}
                  >
                    <MenuItem value="">{t("contact.none")}</MenuItem>
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                    <MenuItem onClick={handleNewTemplateClick}>
                      + {t("contact.newTemplate")}
                    </MenuItem>
                  </Select>
                  {/* Information Tooltip Icon */}
                  <Tooltip
                    title={
                      <h2 style={{ fontSize: "13px" }}>
                        {t("contact.birthdayTemplateDescription")}
                      </h2>
                    }
                    open={tooltipOpen1}
                    onClose={() => setTooltipOpen1(false)}
                    disableHoverListener
                  >
                    <IconButton
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => setTooltipOpen1(true)}
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="countrycode_id-label">
                  {t("contact.countryCode")}
                </InputLabel>
                <Select
                  labelId="countrycode_id-label"
                  id="countrycode_id"
                  value={formData.countrycode_id}
                  label={t("contact.countryCode")}
                  onChange={(e) =>
                    handleChange("countrycode_id", e.target.value)
                  }
                  required
                >
                  {countryCodes.map((code) => (
                    <MenuItem key={code.country_iso} value={code.country_iso}>
                      {`${code.country_iso} (${code.country_code})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("contact.phone")}
                type="number"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                required
              />
            </Grid>

            {variables.map((variable) => (
              <Grid item xs={12} key={variable.id}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {/* Trash Icon on the Left */}
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteVariable(variable.id)}
                    sx={{ mr: 1, color: "error.main" }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>

                  {/* Variable Key and Input */}
                  <Typography sx={{ mr: 2 }}>{variable.key}:</Typography>
                  <TextField
                    fullWidth
                    name={variable.key}
                    value={formData[variable.key] || ""}
                    onChange={(e) => handleChange(variable.key, e.target.value)}
                  />
                </Box>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip
                  title={
                    <h2 style={{ fontSize: "13px" }}>
                      {t("contact.variablesDescription")}
                    </h2>
                  }
                  open={tooltipOpen2}
                  onClose={() => setTooltipOpen2(false)}
                  disableHoverListener
                >
                  <IconButton
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => setTooltipOpen2(true)}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <TextField
                  label={t("contact.newVariableName")}
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  sx={{ mr: 2 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddField}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: "bold",
                    background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                    "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                  }}
                >
                  {t("contact.addVariable")}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "8px",
                  fontWeight: "bold",
                  py: 1.5,
                  mt: 2,
                  background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                  "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                }}
              >
                {isEditing ? t("contact.saveChanges") : t("contact.addContact")}
              </Button>
            </Grid>
          </Grid>
        </form>
        <ConfirmationModal
          open={deleteVariableModalOpen}
          onClose={() => {
            setVariableToDelete(null);
            setDeleteVariableModalOpen(false);
          }}
          onConfirm={() => {
            handleDeleteVariableConfirmation();
          }}
          title={t("contact.deleteVariableTitle")}
          message={t("contact.deleteVariableMessage")}
          showCancel={true}
        />
      </Box>
    </Modal>
  );
}
